import {
    ImageEffectType,
    OpenAIQuality,
    OpenAISize,
    OpenAIBackground,
    OpenAIModel,
    PicwishEnhancementType,
    PicwishUpScaleFactor,
    GeminiAspectRatioType,
    GeminiImageModelsType,
    GeminiImageSizeType,
    GeminiTextToImageModel,
    GeminiTextToImageAspectRatio,
    GeminiTextToImageSize,
    OpenAiTextToImageModel,
    OpenAiTextToImageQuality,
    OpenAiTextToImageBackground,
    OpenAiTextToImageSize,
    OpenRouterTextToImageGeminiModel,
    OpenRouterTextToImageOpenAIModel,
    OpenRouterTextToImageQuality,
    OpenRouterTextToImageBackground,
    OpenRouterTextToImageSize
} from '@/typings/customilyElements/imageEffectType';
import { ImageEffectPreset } from '../types/presets';

export interface EffectOption {
    value:
        | OpenAIQuality
        | OpenAISize
        | OpenAIBackground
        | OpenAIModel
        | PicwishUpScaleFactor
        | PicwishEnhancementType
        | GeminiImageModelsType
        | GeminiImageSizeType
        | GeminiAspectRatioType
        | GeminiTextToImageModel
        | GeminiTextToImageAspectRatio
        | GeminiTextToImageSize
        | OpenAiTextToImageModel
        | OpenAiTextToImageQuality
        | OpenAiTextToImageBackground
        | OpenAiTextToImageSize
        | OpenRouterTextToImageGeminiModel
        | OpenRouterTextToImageOpenAIModel
        | OpenRouterTextToImageQuality
        | OpenRouterTextToImageBackground
        | OpenRouterTextToImageSize;
    label: string;
    /** Kept so saved effects still render it, but not offered for new selections. */
    retired?: boolean;
}

export const CURRENT_GEMINI_IMAGE_MODEL = GeminiImageModelsType.GEMINI_3_1_FLASH_IMAGE;
export const CURRENT_GEMINI_TEXT_TO_IMAGE_MODEL = GeminiTextToImageModel.GEMINI_3_1_FLASH_IMAGE;

export const selectableOptions = (options: EffectOption[], currentValue: EffectOption['value']): EffectOption[] =>
    options.filter((option) => !option.retired || option.value === currentValue);

export const OPENAI_QUALITY_OPTIONS: EffectOption[] = [
    { value: OpenAIQuality.HIGH, label: 'High' },
    { value: OpenAIQuality.MEDIUM, label: 'Medium' },
    { value: OpenAIQuality.LOW, label: 'Low' },
    { value: OpenAIQuality.AUTO, label: 'Auto' }
];

export const OPENAI_BACKGROUND_OPTIONS: EffectOption[] = [
    { value: OpenAIBackground.TRANSPARENT, label: 'Transparent' },
    { value: OpenAIBackground.OPAQUE, label: 'Opaque' },
    { value: OpenAIBackground.AUTO, label: 'Auto' }
];

export const OPENAI_SIZE_OPTIONS: EffectOption[] = [
    { value: OpenAISize.SQUARE, label: 'Square (1024x1024)' },
    { value: OpenAISize.LANDSCAPE, label: 'Landscape (1536x1024)' },
    { value: OpenAISize.PORTRAIT, label: 'Portrait (1024x1536)' },
    { value: OpenAISize.AUTO, label: 'Auto' }
];

export const OPENAI_MODEL_OPTIONS: EffectOption[] = [
    { value: OpenAIModel.GPT_IMAGE_1, label: 'gpt-image-1' },
    { value: OpenAIModel.GPT_IMAGE_1_5, label: 'gpt-image-1.5' },
    { value: OpenAIModel.GPT_IMAGE_1_MINI, label: 'gpt-image-1-mini' },
    { value: OpenAIModel.GPT_IMAGE_2, label: 'gpt-image-2' }
];

export const PICWISH_SCALE_FACTOR_OPTIONS: EffectOption[] = [
    {
        label: 'Auto',
        value: PicwishUpScaleFactor.AUTO
    },
    {
        label: '1x',
        value: PicwishUpScaleFactor.X1
    },
    {
        label: '2x',
        value: PicwishUpScaleFactor.X2
    },
    {
        label: '4x',
        value: PicwishUpScaleFactor.X4
    }
];

export const PICWISH_ENHANCEMENT_TYPE_OPTIONS: EffectOption[] = [
    {
        label: 'Image Enhancement, best for non-portrait images',
        value: PicwishEnhancementType.CLEAN
    },
    {
        label: 'Face Enhancement, best for portraits',
        value: PicwishEnhancementType.FACE
    }
];

export const GEMINI_ASPECT_RATIO_OPTIONS: EffectOption[] = [
    {
        label: '1:1',
        value: GeminiAspectRatioType['1:1']
    },
    {
        label: '2:3',
        value: GeminiAspectRatioType['2:3']
    },
    {
        label: '3:2',
        value: GeminiAspectRatioType['3:2']
    },
    {
        label: '3:4',
        value: GeminiAspectRatioType['3:4']
    },
    {
        label: '4:3',
        value: GeminiAspectRatioType['4:3']
    },
    {
        label: '4:5',
        value: GeminiAspectRatioType['4:5']
    },
    {
        label: '5:4',
        value: GeminiAspectRatioType['5:4']
    },
    {
        label: '9:16',
        value: GeminiAspectRatioType['9:16']
    },
    {
        label: '16:9',
        value: GeminiAspectRatioType['16:9']
    },
    {
        label: '21:9',
        value: GeminiAspectRatioType['21:9']
    }
];

export const GEMINI_IMAGE_SIZE_OPTIONS: EffectOption[] = [
    {
        label: '1K',
        value: GeminiImageSizeType['1K']
    },
    {
        label: '2K',
        value: GeminiImageSizeType['2K']
    },
    {
        label: '4K',
        value: GeminiImageSizeType['4K']
    }
];

export const GEMINI_IMAGE_MODELS_OPTIONS: EffectOption[] = [
    {
        label: GeminiImageModelsType.GEMINI_2_5_FLASH_IMAGE,
        value: GeminiImageModelsType.GEMINI_2_5_FLASH_IMAGE,
        retired: true
    },
    {
        label: GeminiImageModelsType.GEMINI_3_1_FLASH_IMAGE,
        value: GeminiImageModelsType.GEMINI_3_1_FLASH_IMAGE
    },
    {
        label: GeminiImageModelsType.GEMINI_3_PRO_IMAGE,
        value: GeminiImageModelsType.GEMINI_3_PRO_IMAGE
    }
];

export const GEMINI_TEXT_TO_IMAGE_ASPECT_RATIO_OPTIONS: EffectOption[] = [
    {
        label: '1:1',
        value: GeminiTextToImageAspectRatio['1:1']
    },
    {
        label: '2:3',
        value: GeminiTextToImageAspectRatio['2:3']
    },
    {
        label: '3:2',
        value: GeminiTextToImageAspectRatio['3:2']
    },
    {
        label: '3:4',
        value: GeminiTextToImageAspectRatio['3:4']
    },
    {
        label: '4:3',
        value: GeminiTextToImageAspectRatio['4:3']
    },
    {
        label: '4:5',
        value: GeminiTextToImageAspectRatio['4:5']
    },
    {
        label: '5:4',
        value: GeminiTextToImageAspectRatio['5:4']
    },
    {
        label: '9:16',
        value: GeminiTextToImageAspectRatio['9:16']
    },
    {
        label: '16:9',
        value: GeminiTextToImageAspectRatio['16:9']
    },
    {
        label: '21:9',
        value: GeminiTextToImageAspectRatio['21:9']
    }
];

export const GEMINI_TEXT_TO_IMAGE_SIZE_OPTIONS: EffectOption[] = [
    {
        label: '1K',
        value: GeminiTextToImageSize['1K']
    },
    {
        label: '2K',
        value: GeminiTextToImageSize['2K']
    },
    {
        label: '4K',
        value: GeminiTextToImageSize['4K']
    }
];

export const GEMINI_TEXT_TO_IMAGE_MODEL_OPTIONS: EffectOption[] = [
    {
        label: GeminiTextToImageModel.GEMINI_2_5_FLASH_IMAGE,
        value: GeminiTextToImageModel.GEMINI_2_5_FLASH_IMAGE,
        retired: true
    },
    {
        label: GeminiTextToImageModel.GEMINI_3_1_FLASH_IMAGE,
        value: GeminiTextToImageModel.GEMINI_3_1_FLASH_IMAGE
    },
    {
        label: GeminiTextToImageModel.GEMINI_3_PRO_IMAGE,
        value: GeminiTextToImageModel.GEMINI_3_PRO_IMAGE
    }
];

export const OPENAI_TEXT_TO_IMAGE_QUALITY_OPTIONS: EffectOption[] = [
    { value: OpenAiTextToImageQuality.HIGH, label: 'High' },
    { value: OpenAiTextToImageQuality.MEDIUM, label: 'Medium' },
    { value: OpenAiTextToImageQuality.LOW, label: 'Low' },
    { value: OpenAiTextToImageQuality.AUTO, label: 'Auto' }
];

export const OPENAI_TEXT_TO_IMAGE_BACKGROUND_OPTIONS: EffectOption[] = [
    { value: OpenAiTextToImageBackground.TRANSPARENT, label: 'Transparent' },
    { value: OpenAiTextToImageBackground.OPAQUE, label: 'Opaque' },
    { value: OpenAiTextToImageBackground.AUTO, label: 'Auto' }
];

export const OPENAI_TEXT_TO_IMAGE_SIZE_OPTIONS: EffectOption[] = [
    { value: OpenAiTextToImageSize.SQUARE, label: 'Square (1024x1024)' },
    { value: OpenAiTextToImageSize.LANDSCAPE, label: 'Landscape (1536x1024)' },
    { value: OpenAiTextToImageSize.PORTRAIT, label: 'Portrait (1024x1536)' },
    { value: OpenAiTextToImageSize.AUTO, label: 'Auto' }
];

export const OPENAI_TEXT_TO_IMAGE_MODEL_OPTIONS: EffectOption[] = [
    { value: OpenAiTextToImageModel.GPT_IMAGE_1, label: 'gpt-image-1' },
    { value: OpenAiTextToImageModel.GPT_IMAGE_1_5, label: 'gpt-image-1.5' },
    { value: OpenAiTextToImageModel.GPT_IMAGE_1_MINI, label: 'gpt-image-1-mini' },
    { value: OpenAiTextToImageModel.GPT_IMAGE_2, label: 'gpt-image-2' }
];

export const OPENROUTER_TEXT_TO_IMAGE_GEMINI_MODEL_OPTIONS: EffectOption[] = [
    { value: OpenRouterTextToImageGeminiModel.GEMINI_2_5_FLASH_IMAGE, label: 'Gemini 2.5 Flash Image', retired: true },
    { value: OpenRouterTextToImageGeminiModel.GEMINI_3_1_FLASH_IMAGE, label: 'Gemini 3.1 Flash Image' },
    { value: OpenRouterTextToImageGeminiModel.GEMINI_3_PRO_IMAGE, label: 'Gemini 3 Pro Image' }
];

export const OPENROUTER_TEXT_TO_IMAGE_OPENAI_MODEL_OPTIONS: EffectOption[] = [
    { value: OpenRouterTextToImageOpenAIModel.GPT_IMAGE_1, label: 'OpenAI gpt-image-1' },
    { value: OpenRouterTextToImageOpenAIModel.GPT_IMAGE_1_5, label: 'OpenAI gpt-image-1.5' },
    { value: OpenRouterTextToImageOpenAIModel.GPT_IMAGE_1_MINI, label: 'OpenAI gpt-image-1-mini' },
    { value: OpenRouterTextToImageOpenAIModel.GPT_IMAGE_2, label: 'OpenAI gpt-image-2' }
];

export const OPENROUTER_TEXT_TO_IMAGE_QUALITY_OPTIONS: EffectOption[] = [
    { value: OpenRouterTextToImageQuality.HIGH, label: 'High' },
    { value: OpenRouterTextToImageQuality.MEDIUM, label: 'Medium' },
    { value: OpenRouterTextToImageQuality.LOW, label: 'Low' },
    { value: OpenRouterTextToImageQuality.AUTO, label: 'Auto' }
];

export const OPENROUTER_TEXT_TO_IMAGE_BACKGROUND_OPTIONS: EffectOption[] = [
    { value: OpenRouterTextToImageBackground.TRANSPARENT, label: 'Transparent' },
    { value: OpenRouterTextToImageBackground.OPAQUE, label: 'Opaque' },
    { value: OpenRouterTextToImageBackground.AUTO, label: 'Auto' }
];

export const OPENROUTER_TEXT_TO_IMAGE_SIZE_OPTIONS: EffectOption[] = [
    { value: OpenRouterTextToImageSize.SQUARE, label: 'Square (1024x1024)' },
    { value: OpenRouterTextToImageSize.LANDSCAPE, label: 'Landscape (1536x1024)' },
    { value: OpenRouterTextToImageSize.PORTRAIT, label: 'Portrait (1024x1536)' },
    { value: OpenRouterTextToImageSize.LANDSCAPE_WIDE, label: 'Landscape Wide (1792x1024)' },
    { value: OpenRouterTextToImageSize.PORTRAIT_WIDE, label: 'Portrait Wide (1024x1792)' },
    { value: OpenRouterTextToImageSize.AUTO, label: 'Auto' }
];

export const createEffectFromPreset = (preset: ImageEffectPreset): ImageEffectType => {
    const baseConfig = { ...preset.effectConfig };

    if (!baseConfig.id || baseConfig.id === preset.id) {
        baseConfig.id = crypto.randomUUID();
    }

    return baseConfig;
};
